// Keeping the most recently MINTED copy-link URL reachable for a little while. No
// React, no fetch (lib/ convention). Sibling of ./secretStash.js rather than part
// of it — same storage medium and the same never-throws discipline, but a
// different key space, a different lifetime, and different clearing rules, and
// Task 1's principle is that distinct storage concerns get distinct modules.
//
// The two are worth telling apart, because they hold the same secret at opposite
// ends of its life:
//
//   secretStash — the RECIPIENT's side. Carries a secret arriving in a fragment
//     across one auth round trip. 10-minute TTL, keyed by copyId, cleared on a
//     terminal redemption outcome.
//   this file   — the OWNER's side. Carries a URL the owner just minted, so they
//     can put it on the clipboard again. 60-minute TTL, keyed by forecastId,
//     cleared on revoke.
//
// WHY IT HAS TO EXIST. Mint returns the capability URL exactly once
// (server/handlers/copyLinks.js:442-446); the server keeps only `secret_hash`
// (:333) and `GET /api/copy-links` returns nothing a URL can be rebuilt from. Held
// in memory alone, the URL dies on the next reload — and the way a real person
// loses it is mundane and total: mint, copy, copy something ELSE before pasting,
// and the live capability is gone for its whole 90-day life with no way back.
//
// WHY sessionStorage AND NOT localStorage. The entry is a live capability at rest.
// `sessionStorage` is bounded by the TAB, so closing it disposes of the capability;
// `localStorage` would leave a 90-day link sitting in a shared or kiosk browser for
// whoever sits down next. The frustration this fixes is minutes-to-an-hour, so the
// shorter lifetime costs nothing real.
//
// THE SERVER NOW PERSISTS THE URL TOO, AND THIS MODULE IS THE VESTIGE. This header
// used to say "not a licence to persist server-side", on an aggregation argument:
// one Postgres read yielding every live capability for every user. That argument
// was made, and it lost — deliberately, because the panel was listing links their
// owner could not copy. `forecast_copy_links.secret` (migration 1786060800000) holds
// the token and `GET /api/copy-links` returns each row's URL to its owner, scoped by
// `owner_user_id`. The reasoning, and the three boundaries that keep it narrow, are
// in docs/capsules/forecast-copy-links.md — read that before re-litigating either
// direction.
//
// What follows is therefore a fallback whose remaining reachability is doubtful:
// `selectCurrentLink` prefers the server's URL, and the case this would still serve
// (a live row with no served URL whose id is nonetheless in THIS tab's stash) may be
// unreachable. Removing it is a tracked follow-up, not a licence to depend on it.
//
// FOUR RULES, all enforced below:
//   1. Keyed per FORECAST, so opening another forecast can never surface a URL
//      minted for a different one.
//   2. Self-expiring on a TTL far shorter than the link's own life.
//   3. Cleared when the link is revoked — offering a revoked URL is worse than
//      having lost it, because the owner would hand someone a link that 410s.
//   4. Never logged, never put in a URL, a query string, or the address bar. It is
//      the same live capability the fragment carries. Nothing in this module
//      writes to `console`, and the KEY carries only the forecast id.
//
// Nothing here throws. Reading `sessionStorage` itself throws in a sandboxed frame
// and `setItem` throws in Safari private mode and on a full quota; a storage that
// refuses must degrade to "no stash" — i.e. exactly the in-memory-only behaviour
// this module improves on — never to a broken panel.

/**
 * How long a minted URL stays offered.
 *
 * An hour, against `secretStash`'s ten minutes, because the two windows are sized
 * for different things: that one covers a single interactive login, this one covers
 * "I minted it, got interrupted, and came back to send it". It is still three
 * orders of magnitude below the link's own 90-day life, and the tab lifetime bounds
 * it harder than the TTL does in practice.
 */
import { defaultSessionStorage } from "../util/sessionStorage.js";

export const MINTED_STASH_TTL_MS = 60 * 60 * 1000;

/** Namespaced per FORECAST — rule 1. Carries no part of the URL or the secret. */
export function mintedStashKey(forecastId) {
  return `soothCopyMinted:${forecastId}`;
}

/**
 * Is this a URL worth offering as a capability?
 *
 * EXPORTED so `useMintedLinkStash` can gate its in-memory fallback on exactly the
 * same predicate this module gates storage on. They diverged at first, and mutation
 * testing found the consequence: on a browser that refuses storage, a malformed
 * mint body was held in memory that a working browser would have rejected — two
 * behaviours from one input, decided by whether `setItem` happened to work.
 *
 * The fragment check is the substantive one: the secret rides in the fragment, so a
 * URL WITHOUT one is not a capability at all — copying it would hand someone a link
 * that cannot redeem, which is the one lie this surface must not tell. A bare
 * `"…#"` fails too; there has to be something after the hash.
 *
 * @param {unknown} url
 * @returns {boolean}
 */
export function isCapabilityUrl(url) {
  if (typeof url !== "string" || !url) return false;
  const hash = url.indexOf("#");
  return hash > 0 && hash < url.length - 1;
}

/**
 * Park the URL a mint just returned.
 *
 * @param {string} forecastId  the SERVER uuid the link was minted against
 * @param {{copyId?: unknown, url?: unknown}} minted  the mint response, verbatim
 * @param {{storage?: object|null, now?: number}} [opts]
 * @returns {boolean} true only if the value is retrievable afterwards. Callers use
 *   the false case to fall back to holding it in memory for this page load.
 */
export function stashMintedUrl(forecastId, minted, { storage, now = Date.now() } = {}) {
  const store = storage === undefined ? defaultSessionStorage() : storage;
  if (!store || !forecastId) return false;
  const copyId = minted && typeof minted.copyId === "string" ? minted.copyId : "";
  const url = minted ? minted.url : null;
  if (!copyId || !isCapabilityUrl(url)) return false;
  try {
    store.setItem(
      mintedStashKey(forecastId),
      JSON.stringify({ c: copyId, u: url, e: now + MINTED_STASH_TTL_MS }),
    );
    return true;
  } catch {
    return false;
  }
}

/**
 * The URL stashed for this forecast, or null.
 *
 * Anything unusable — absent, corrupt, expired, or no longer shaped like a
 * capability — is REMOVED as it is rejected, the same discipline `readStashedSecret`
 * follows. That removal makes this function impure, and callers do run it during
 * render; it is idempotent and only ever deletes a value that is already dead, so
 * the alternative (leaving an expired capability sitting in storage until the tab
 * closes, because dropping it would need a state-setting effect the linter refuses)
 * is the worse trade.
 *
 * @param {string} forecastId
 * @param {{storage?: object|null, now?: number}} [opts]
 * @returns {{copyId: string, url: string}|null}
 */
export function readMintedUrl(forecastId, { storage, now = Date.now() } = {}) {
  const store = storage === undefined ? defaultSessionStorage() : storage;
  if (!store || !forecastId) return null;
  let raw;
  try {
    raw = store.getItem(mintedStashKey(forecastId));
  } catch {
    return null;
  }
  if (typeof raw !== "string" || !raw) return null;

  const drop = () => {
    clearMintedUrl(forecastId, { storage: store });
    return null;
  };

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return drop();
  }
  if (!parsed || typeof parsed !== "object") return drop();
  if (typeof parsed.c !== "string" || !parsed.c) return drop();
  if (!Number.isFinite(parsed.e) || now >= parsed.e) return drop();
  if (!isCapabilityUrl(parsed.u)) return drop();
  return { copyId: parsed.c, url: parsed.u };
}

/**
 * Remove the entry — rule 3, and the only remover besides an expiry.
 *
 * @param {string} forecastId
 * @param {{storage?: object|null}} [opts]
 * @returns {void}
 */
export function clearMintedUrl(forecastId, { storage } = {}) {
  const store = storage === undefined ? defaultSessionStorage() : storage;
  if (!store || !forecastId) return;
  try {
    store.removeItem(mintedStashKey(forecastId));
  } catch {
    /* a store that refuses removeItem is a store we never wrote to */
  }
}
